// Dimension arithmetic only; positive differences do not establish physical fit.
const units={in:2.54,cm:1};
function number(value,{zero=false}={}){
  if(value===null||value===undefined||value==='')return null;
  if(typeof value!=='number'||!Number.isFinite(value)||value<(zero?0:Number.MIN_VALUE))throw new TypeError('Use a finite positive number, or null when unknown.');
  return value;
}
function cm(value,unit,options){
  if(!Object.hasOwn(units,unit))throw new TypeError('Choose in or cm explicitly.');
  const n=number(value,options);
  if(n===null)return null;
  const converted=n*units[unit];
  if(!Number.isFinite(converted))throw new TypeError('The converted measurement must be finite.');
  return converted;
}
function difference(available,body){
  return available===null||body===null?null:Number((available-body).toFixed(8));
}
export function compareCrateDimensions({dog,crates}){
  if(!dog||!Array.isArray(crates)||crates.length<1||crates.length>3)throw new TypeError('Compare one to three crates.');
  const length=cm(dog.noseToTailBase,dog.unit);
  const standing=cm(dog.standingHeight,dog.unit),sitting=cm(dog.sittingHeight,dog.unit);
  const height=standing===null||sitting===null?null:Math.max(standing,sitting);
  return crates.map((c,index)=>{
    if(!['internal','external','unknown'].includes(c.dimensionType))throw new TypeError('Identify the dimension type.');
    const l=cm(c.length,c.unit),w=cm(c.width,c.unit),h=cm(c.height,c.unit);
    const padding=cm(c.padThickness,c.unit,{zero:true});
    if(c.dimensionType==='internal'&&padding!==null&&h!==null&&padding>=h)throw new RangeError('Pad thickness must be less than the internal height.');
    const known=c.dimensionType==='internal';
    const usableHeight=known&&h!==null&&padding!==null?h-padding:null;
    const lengthDifference=difference(known?l:null,length),heightDifference=difference(usableHeight,height);
    const missing=[];
    if(!known)missing.push('CONFIRM_INTERNAL_NOT_EXTERNAL_DIMENSIONS');
    if(length===null)missing.push('MEASURE_DOG_NOSE_TO_TAIL_BASE');
    if(height===null)missing.push('MEASURE_BOTH_STANDING_AND_SITTING_HEIGHT');
    if(l===null||w===null||h===null)missing.push('COMPLETE_CRATE_DIMENSIONS');
    if(padding===null)missing.push('ENTER_PAD_THICKNESS_OR_EXPLICIT_ZERO');
    const shortfalls=[];
    if(lengthDifference!==null&&lengthDifference<=0)shortfalls.push('NO_LENGTH_CLEARANCE');
    if(heightDifference!==null&&heightDifference<=0)shortfalls.push('NO_HEIGHT_CLEARANCE');
    return {index,unit:'cm',internalLength:known?l:null,internalWidth:known?w:null,usableHeight,
      lengthDifference,heightDifference,missing,shortfalls,
      status:shortfalls.length?'MEASUREMENT_SHORTFALL':missing.length?'MORE_MEASUREMENTS_NEEDED':'IN_PERSON_FIT_CHECK_STILL_REQUIRED',
      recommendedCrateSize:null,guaranteedFit:false,
      nextChecks:['Check the exact maker instructions and dimension labels.','With the actual bedding in place, check comfortable standing, sitting, turning and lying down; dimensions alone do not establish this.','For a growing puppy, remeasure; do not infer adult dimensions from a breed label.','This is a home dimension comparison, not airline eligibility, vehicle safety, or guidance on confinement duration.']};
  });
}


/** Mount one inline comparison. All displayed copy comes from its Liquid locale data. */
export function mountCrateComparison(root) {
  const form = root?.querySelector('[data-form]');
  const results = root?.querySelector('[data-results]');
  const status = root?.querySelector('[data-status]');
  const cards = root?.querySelector('[data-result-cards]');
  const count = root?.querySelector('[data-count]');
  const dogGroup = root?.querySelector('[data-dog]');
  const dogSummary = root?.querySelector('[data-dog-summary]');
  const resultHeading = root?.querySelector('[data-result-heading]');
  const copyNode = root?.querySelector('[data-copy]');
  const fieldsets = [...(form?.querySelectorAll('[data-crate]') || [])];
  if (!form || !results || !status || !cards || !count || !dogGroup || !dogSummary || !resultHeading || !copyNode || fieldsets.length !== 3) return () => {};
  if ([dogGroup, ...fieldsets].some(group => !group.querySelector('[data-unit]'))) return () => {};
  let copy;
  try { copy = JSON.parse(copyNode.textContent); } catch { return () => {}; }
  if (!copy || Object.values(copy).some(value => typeof value !== 'string')) return () => {};
  const requiredCopy = 'crate,internal,external,unknown_type,ui_unknown,ui_changed,ui_units_converted,ui_conversion_error,ui_units_cleared,ui_invalid,ui_dog_summary,ui_spec_summary,ui_status_shortfall,ui_status_missing,ui_status_check,ui_missing_internal,ui_missing_length,ui_missing_height,ui_missing_crate,ui_missing_bedding,ui_short_length,ui_short_height,ui_interior_length,ui_interior_width,ui_usable_height,ui_length_difference,ui_height_difference,ui_ready_one,ui_ready_many,ui_bedding_error,ui_number_error,ui_cleared,ui_unavailable'.split(',');
  if (requiredCopy.some(key => !copy[key] || /translation missing/i.test(copy[key]))) return () => {};
  const controller = new AbortController();
  const options = {signal: controller.signal};
  let formatter;
  try { formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {maximumFractionDigits: 2}); }
  catch { formatter = new Intl.NumberFormat('en-US', {maximumFractionDigits: 2}); }
  const pretty = value => formatter.format(value);
  const measure = (value, unit) => value === null ? copy.ui_unknown : pretty(value) + ' ' + unit;
  const fill = (template, values) => template.replace(/__([A-Z]+)__/g, (match, key) => values[key] ?? match);
  const missing = {
    CONFIRM_INTERNAL_NOT_EXTERNAL_DIMENSIONS: copy.ui_missing_internal,
    MEASURE_DOG_NOSE_TO_TAIL_BASE: copy.ui_missing_length,
    MEASURE_BOTH_STANDING_AND_SITTING_HEIGHT: copy.ui_missing_height,
    COMPLETE_CRATE_DIMENSIONS: copy.ui_missing_crate,
    ENTER_PAD_THICKNESS_OR_EXPLICIT_ZERO: copy.ui_missing_bedding
  };
  const states = {
    MEASUREMENT_SHORTFALL: copy.ui_status_shortfall,
    MORE_MEASUREMENTS_NEEDED: copy.ui_status_missing,
    IN_PERSON_FIT_CHECK_STILL_REQUIRED: copy.ui_status_check
  };
  const shortfalls = {NO_LENGTH_CLEARANCE: copy.ui_short_length, NO_HEIGHT_CLEARANCE: copy.ui_short_height};
  const element = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  const clearResults = message => {
    results.hidden = true;
    cards.replaceChildren();
    status.textContent = message;
    delete status.dataset.error;
  };
  const syncCount = () => {
    for (const [index, fieldset] of fieldsets.entries()) {
      fieldset.hidden = index >= Number(count.value);
      fieldset.disabled = fieldset.hidden;
    }
  };
  const readGroup = group => {
    const value = {unit: group.querySelector('[data-unit]').value};
    for (const input of group.querySelectorAll('[data-number]')) value[input.dataset.number] = input.value === '' ? null : input.valueAsNumber;
    const type = group.querySelector('[data-type]');
    if (type) value.dimensionType = type.value;
    value.reference = group.querySelector('[data-reference]')?.value.trim().slice(0, 180) || '';
    return value;
  };
  const clearInvalid = () => {
    for (const input of form.querySelectorAll('[aria-invalid="true"]')) input.removeAttribute('aria-invalid');
  };
  form.addEventListener('input', () => { clearInvalid(); clearResults(copy.ui_changed); }, options);
  form.addEventListener('change', event => {
    const select = event.target;
    clearInvalid();
    clearResults(copy.ui_changed);
    if (select instanceof HTMLSelectElement && select.matches('[data-unit]')) {
      const previous = select.dataset.previous;
      const next = select.value;
      const group = select.closest('fieldset');
      if (group && units[previous] && units[next] && previous !== next) {
        let cleared = false;
        for (const input of group.querySelectorAll('[data-number]')) {
          if (input.value === '') continue;
          const converted = input.valueAsNumber * units[previous] / units[next];
          const rounded = Number(converted.toFixed(8));
          if (Number.isFinite(rounded) && (rounded > 0 || (input.dataset.number === 'padThickness' && rounded === 0))) input.value = String(rounded);
          else { input.value = ''; cleared = true; }
        }
        status.textContent = cleared ? copy.ui_conversion_error : copy.ui_units_converted;
      } else if (group && previous && !next) {
        for (const input of group.querySelectorAll('[data-number]')) input.value = '';
        status.textContent = copy.ui_units_cleared;
      }
      select.dataset.previous = next;
    }
    syncCount();
  }, options);
  form.addEventListener('submit', event => {
    event.preventDefault();
    clearResults('');
    if (!form.checkValidity()) {
      status.textContent = copy.ui_invalid;
      status.dataset.error = 'true';
      for (const input of form.querySelectorAll('input:invalid, select:invalid')) input.setAttribute('aria-invalid', 'true');
      form.querySelector('input:invalid, select:invalid')?.focus();
      form.reportValidity();
      return;
    }
    const dog = readGroup(dogGroup);
    const crates = fieldsets.filter(fieldset => !fieldset.disabled).map(readGroup);
    try {
      const comparisons = compareCrateDimensions({dog, crates});
      dogSummary.textContent = fill(copy.ui_dog_summary, {LENGTH: measure(dog.noseToTailBase, dog.unit), STANDING: measure(dog.standingHeight, dog.unit), SITTING: measure(dog.sittingHeight, dog.unit), UNIT: dog.unit});
      for (const [index, comparison] of comparisons.entries()) {
        const section = element('section', undefined, 'b2h-crate-comparison__card');
        section.dataset.result = String(index);
        section.dataset.state = comparison.status;
        section.append(element('h3', copy.crate + ' ' + String.fromCharCode(65 + index)));
        section.append(element('p', states[comparison.status], 'b2h-crate-comparison__state'));
        const spec = crates[index];
        if (spec.reference) section.append(element('p', spec.reference));
        section.append(element('p', fill(copy.ui_spec_summary, {
          TYPE: spec.dimensionType === 'unknown' ? copy.unknown_type : copy[spec.dimensionType],
          DIMENSIONS: [spec.length, spec.width, spec.height].map(value => value === null ? copy.ui_unknown : pretty(value)).join(' × ') + ' ' + spec.unit,
          BEDDING: measure(spec.padThickness, spec.unit)
        })));
        const list = element('dl');
        for (const [label, value] of [[copy.ui_interior_length, comparison.internalLength], [copy.ui_interior_width, comparison.internalWidth], [copy.ui_usable_height, comparison.usableHeight], [copy.ui_length_difference, comparison.lengthDifference], [copy.ui_height_difference, comparison.heightDifference]]) {
          list.append(element('dt', label), element('dd', measure(value === null ? null : value / units[dog.unit], dog.unit)));
        }
        section.append(list);
        const issues = [...comparison.shortfalls.map(key => shortfalls[key]), ...comparison.missing.map(key => missing[key])];
        if (issues.length) {
          const warnings = element('ul');
          for (const text of issues) warnings.append(element('li', text));
          section.append(warnings);
        }
        cards.append(section);
      }
      results.hidden = false;
      status.textContent = comparisons.length === 1 ? copy.ui_ready_one : fill(copy.ui_ready_many, {COUNT: String(comparisons.length)});
      resultHeading.focus();
    } catch (error) {
      status.textContent = error instanceof RangeError ? copy.ui_bedding_error : copy.ui_number_error;
      status.dataset.error = 'true';
      if (error instanceof RangeError) {
        const index = crates.findIndex(crate => crate.dimensionType === 'internal' && crate.height !== null && crate.padThickness !== null && crate.padThickness >= crate.height);
        const input = fieldsets[index]?.querySelector('[data-number="padThickness"]');
        input?.setAttribute('aria-invalid', 'true');
        input?.focus();
      }
    }
  }, options);
  form.addEventListener('reset', event => {
    event.preventDefault();
    clearInvalid();
    for (const input of form.querySelectorAll('input')) input.value = '';
    for (const unit of form.querySelectorAll('[data-unit]')) { unit.value = ''; delete unit.dataset.previous; }
    for (const type of form.querySelectorAll('[data-type]')) type.value = 'unknown';
    count.value = '2';
    syncCount();
    clearResults(copy.ui_cleared);
  }, options);
  root.querySelector('[data-print]')?.addEventListener('click', () => { if (!results.hidden) window.print(); }, options);
  syncCount();
  form.hidden = false;
  return () => { controller.abort(); form.hidden = true; clearResults(copy.ui_unavailable); };
}

// No shared modal/carousel base fits an inline form. The native lifecycle keeps
// Section Rendering removal/reconnection from leaving stale event handlers.
if (typeof customElements !== 'undefined' && !customElements.get('b2h-crate-comparison')) {
  customElements.define('b2h-crate-comparison', class extends HTMLElement {
    connectedCallback() {
      this.dispose?.();
      this.dispose = mountCrateComparison(this);
    }
    disconnectedCallback() {
      this.dispose?.();
      this.dispose = null;
    }
  });
}

